import { useQuery } from '@apollo/client';
import { Box } from 'braid-design-system';
import queryString from 'query-string';
import React from 'react';
import { useLocation } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import { Error as GenericError } from 'src/components/Error/Error';
import {
  GetListingProductsForPaymentPageDocument,
  ListingProductCode,
  type ListingProduct,
} from 'src/generated/graphql';
import type { ProductBundle } from 'src/types/productBundle';
import {
  findListingProductByProductCode,
  findListingProducts,
  isUpsellProduct,
  initProductBundle,
  isProductWithBoost,
} from 'src/utils/listingProducts';

import { PaymentContainer } from '../PaymentContainer';

import { AcceleratePaymentSuccess } from './AcceleratePaymentSuccess';
import { BoostPaymentSuccess } from './BoostPaymentSuccess';
import { GenericPaymentSuccess } from './GenericPaymentSuccess';
import { usePaymentSuccessPostListing } from './PostListingFeedback';

const PaymentSuccessContainer = () => {
  const location = useLocation();
  const { productToken: productTokens } = queryString.parseUrl(
    location.search,
  ).query;

  const { data, error, loading } = useQuery(
    GetListingProductsForPaymentPageDocument,
  );

  if (loading) {
    return (
      <PaymentContainer>
        <Box data={{ testid: 'payment-success-loader' }}>
          <CustomLoader />
        </Box>
      </PaymentContainer>
    );
  }

  const selectedListingProducts = findListingProducts(
    data?.listingProducts,
    productTokens,
  );
  if (error || !data || !selectedListingProducts) {
    return (
      <PaymentContainer>
        <GenericError />
      </PaymentContainer>
    );
  }

  const productBundleResult = initProductBundle(
    data.listingProducts,
    selectedListingProducts,
  );
  if (productBundleResult.type === 'error') {
    return (
      <PaymentContainer>
        <GenericError />
      </PaymentContainer>
    );
  }

  return (
    <PaymentSuccess
      listingProducts={data.listingProducts}
      selectedListingProducts={selectedListingProducts}
      productBundle={productBundleResult.value}
    />
  );
};

interface PaymentSuccessProps {
  listingProducts: ListingProduct[];
  selectedListingProducts: ListingProduct[];
  productBundle: ProductBundle;
}

const PaymentSuccess = ({
  listingProducts,
  selectedListingProducts,
  productBundle,
}: PaymentSuccessProps) => {
  const postListingStatus = usePaymentSuccessPostListing(productBundle);

  if (productBundle.info.productCode === ListingProductCode.Accelerate) {
    return <AcceleratePaymentSuccess />;
  }

  if (productBundle.isBoostBundle) {
    return <BoostPaymentSuccess variant="email-and-search-boost" />;
  }
  if (
    !productBundle.availableEmailBoost &&
    isProductWithBoost(productBundle.info.productCode)
  ) {
    return <BoostPaymentSuccess variant="email-boost-only" />;
  }
  if (
    !productBundle.availableSearchBoost &&
    isProductWithBoost(productBundle.info.productCode)
  ) {
    // Upsell products represent buying a base listing product. These base listing
    // products can be posted at time of purchase, hence the "post listing" behaviour
    if (isUpsellProduct(productBundle.info.productCode)) {
      return (
        <BoostPaymentSuccess
          variant="search-boost-only"
          baseContract={findListingProductByProductCode(
            listingProducts,
            productBundle.contract,
          )}
          postListingStatus={postListingStatus}
        />
      );
    }
    return <BoostPaymentSuccess variant="search-boost-only" />;
  }

  const firstProduct = selectedListingProducts[0];
  return (
    <GenericPaymentSuccess
      product={firstProduct}
      postListingStatus={postListingStatus}
    />
  );
};

export default PaymentSuccessContainer;
